import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import moment from 'moment';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { EscalationComponent } from '../../../components/escalation/escalation.component';
import { ConfigConstants } from '../../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import { CONNECTION_ERROR_CODES } from '../../../enums/connection-error-codes.enum';
import { AppNotificationType } from '../../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../../services/app-notification/app-notification.service';
import {
    NotificationDataInstanceToken,
    NotificationDataInterface,
} from '../../../interfaces/notification-data.interface';
import { OdataEscalationService } from '../../../services/escalation/odata-escalation.service';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../../interfaces/synchronization.interface';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import {
    addEscalationTimerEntry,
    cancelEscalationTimer,
    checkEscalationRequired,
    clearEscalationTimerEntries,
    escalationTimerFired,
    escalationTimersLoadedFromStorage,
    hydrateEscalations,
    removeEscalationTimerEntry,
    scheduleEscalationTimer,
    submitEscalationStatus,
    submitEscalationStatusError,
    submitEscalationStatusSuccess,
    triggerShowEscalationPopup,
} from '../actions/escalation.actions';
import {
    selectEscalationLoaded,
    selectEscalationTimerExists,
    selectEscalationTimerHandle,
} from '../selectors/escalation.selector';
import { ESCALATION_FEATURE_KEY, EscalationTimer, OdataEscalation } from '../escalation-collection.state';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import { selectCanEscalateById, selectEntriesInEtaOrHigher, updateNotificationStatusFields } from '../../notification';

@Injectable()
export class EscalationEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly odataEscalationService: OdataEscalationService,
        private readonly modalService: BsModalService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
        @Inject(SynchronizationInstanceToken) private readonly synchronizationService: SynchronizationInterface,
    ) {}

    /**
     * Iterates all in-progress notifications and dispatches `scheduleEscalationTimer`
     * for each Service-type notification that is in OSR status, not yet escalated,
     * and has no timer registered.
     */
    public checkEscalationRequired$ = createEffect(() =>
        this.actions$.pipe(
            ofType(checkEscalationRequired),
            mergeMap(() => {
                const timerActions = this.notificationService
                    .getAllNotificationsInProgress()
                    .filter((n) => n?.notificationType === ConfigConstants.NotificationTypes.Service)
                    .filter((n) => {
                        const status = this.notificationService.getStatus(n);
                        return status.statusCode === NOTIFICATION_STATUS_CODE.OSR;
                    })
                    .filter((n) => !this.notificationService.getEscalated(n))
                    .filter((n) => {
                        return !this.store.selectSignal(selectEscalationTimerExists(n.identifier))();
                    })
                    .map((n) => {
                        const status = this.notificationService.getStatus(n);
                        const elapsedHours = moment.duration(moment().diff(status.startTime)).asHours();
                        return scheduleEscalationTimer({
                            notificationId: NotificationStateUtilities.toState(n.identifier),
                            notificationType: n.notificationType,
                            elapsedHours,
                        });
                    });
                return from(timerActions);
            }),
        ),
    );

    /**
     * Creates a `window.setTimeout` for the notification and records the handle in
     * the store. When the timeout fires it dispatches `escalationTimerFired`.
     * Ignored for non-Service notification types (guard is here, not in the service).
     */
    public scheduleEscalationTimer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(scheduleEscalationTimer),
            filter(({ notificationType }) => notificationType === ConfigConstants.NotificationTypes.Service),
            map(({ notificationId, elapsedHours }) => {
                const remainingHours =
                    elapsedHours >= ConfigConstants.escalationTimeoutAlertTimingInHours
                        ? 0
                        : ConfigConstants.escalationTimeoutAlertTimingInHours - elapsedHours;

                const hoursInMilliSeconds =
                    remainingHours *
                    ConfigConstants.timeMultiplier *
                    ConfigConstants.timeMultiplier *
                    ConfigConstants.milliSecToSecConversionFactor;

                const timerHandle = window.setTimeout(
                    () => this.store.dispatch(escalationTimerFired({ notificationId })),
                    hoursInMilliSeconds,
                );

                return addEscalationTimerEntry({ notificationId, timerHandle });
            }),
        ),
    );

    /**
     * Clears the `window.setTimeout` for a notification and removes it from the store.
     */
    public cancelEscalationTimer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cancelEscalationTimer),
            map(({ notificationId }) => {
                const timerHandle = this.store.selectSignal(selectEscalationTimerHandle(notificationId))();
                if (timerHandle !== undefined) {
                    window.clearTimeout(timerHandle);
                }
                return removeEscalationTimerEntry({ notificationId });
            }),
        ),
    );

    /**
     * When a scheduled timer fires, verifies the notification is still in OSR status
     * before requesting the popup. Logs an error and drops the action otherwise.
     */
    public escalationTimerFired$ = createEffect(() =>
        this.actions$.pipe(
            ofType(escalationTimerFired),
            map(({ notificationId }) => {
                const notification = this.notificationService
                    .getAllNotificationsInProgress()
                    .find((n) => NotificationStateUtilities.compare(n.identifier, notificationId));

                if (!notification) {
                    return null;
                }

                const status = this.notificationService.getStatus(notification);
                if (status.statusCode !== NOTIFICATION_STATUS_CODE.OSR) {
                    this.log.error(
                        `EscalationEffects: escalationTimerFired: Tried to escalate notification ${notificationId.frontId} without OSR status!`,
                    );
                    return null;
                }

                return triggerShowEscalationPopup({ notificationId });
            }),
            filter((action): action is ReturnType<typeof triggerShowEscalationPopup> => action !== null),
        ),
    );

    /**
     * Opens the escalation modal for the notification that reached OSR timeout.
     */
    public showEscalationPopup$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(triggerShowEscalationPopup),
                tap(({ notificationId }) => {
                    const notification = this.notificationService
                        .getAllNotificationsInProgress()
                        .find((n) => NotificationStateUtilities.compare(n.identifier, notificationId));

                    if (!notification) {
                        return;
                    }

                    this.modalService.show(EscalationComponent, {
                        backdrop: 'static',
                        class: 'modal-dialog modal-fullscreen-lg-down',
                        initialState: { notification },
                        ariaLabelledBy: 'escalation-modal-title',
                    });
                }),
            ),
        { dispatch: false },
    );

    /**
     * Executes the escalation save chain: OData insert → mark escalated →
     * send offline data. Dispatches `escalationStatusSaved` on success or
     * `escalationStatusFailed` on error (error notification is also set here).
     */
    public submitEscalationStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitEscalationStatus),
            mergeMap(({ escalationData }) => {
                const notification = this.store
                    .selectSignal(selectEntriesInEtaOrHigher)()
                    .find((n) => NotificationStateUtilities.compare(n.notificationId, escalationData.notificationId));

                if (!notification) {
                    return of(submitEscalationStatusError({ notificationId: escalationData.notificationId }));
                }

                const escalationState: OdataEscalation = {
                    MessageType: escalationData.messageType,
                    Date: escalationData.date,
                    Time: escalationData.time,
                    EscalationLtextSet: escalationData.escalationLText,
                    FrontId: escalationData.notificationId.frontId,
                    NotificationNo: escalationData.notificationId.qmNum,
                    TaskNumber: escalationData.notificationId.maNum,
                    TimeZone: '',
                    UTCSign: moment().utcOffset() >= 0 ? '+' : '-',
                    UTCDiff: moment
                        .utc(moment.duration(moment().utcOffset(), 'minutes').asMilliseconds())
                        .format(ConfigConstants.formatXSDTimeDuration),
                };

                return this.odataEscalationService.saveEscalationStatusOutModel(escalationState).pipe(
                    tap(() => {
                        this.store.dispatch(
                            updateNotificationStatusFields({
                                id: notification.notificationId,
                                changes: {
                                    escalated: this.store.selectSignal(
                                        selectCanEscalateById(notification.notificationId),
                                    )(),
                                },
                            }),
                        );
                    }),
                    mergeMap(() => this.synchronizationService.sendOfflineData()),
                    map(() => submitEscalationStatusSuccess({ notificationId: escalationData.notificationId })),
                    catchError((error) => {
                        this.log.error(
                            'EscalationEffects: saveEscalationStatus$ - error: ' +
                                JSON.stringify(error, Object.getOwnPropertyNames(error)),
                        );
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.ERROR,
                            error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                                ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                                : <string>this.translate.instant('ESCALATION_SUBMIT_ERROR_TITLE'),
                            error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                                ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                                : <string>this.translate.instant('ESCALATION_SUBMIT_ERROR_BODY'),
                        );
                        return of(submitEscalationStatusError({ notificationId: escalationData.notificationId }));
                    }),
                );
            }),
        ),
    );

    /** Persist the slice whenever a timer entry is added, removed, or cleared. */
    public persistEscalationTimers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addEscalationTimerEntry, removeEscalationTimerEntry, clearEscalationTimerEntries),
            map(() => persistSlice({ featureKey: ESCALATION_FEATURE_KEY })),
        ),
    );

    /** Trigger rehydration from IndexedDB on init, but only before the first hydration. */
    public hydrateEscalationTimersOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(hydrateEscalations),
            switchMap(() => this.store.select(selectEscalationLoaded)),
            filter((loaded) => !loaded),
            map(() => hydrateSlice({ featureKey: ESCALATION_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the escalation slice, unless already hydrated. */
    public applyHydratedEscalationTimers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === ESCALATION_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectEscalationLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                escalationTimersLoadedFromStorage({
                    timerEntries: (data as EscalationTimer[] | undefined) ?? [],
                }),
            ),
        ),
    );
}
